import React from 'react';
import { connect } from 'react-redux';

import {
    APP_STATUS_MESSAGES,
    APP_STATUS_LINKS
} from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { ctaHandlers } from '@utils/dashboard/stateSpecificUtils';
import AppStatus from '@components/individualPortal/dashboard/AppStatus';
import { showModal } from '@actions/common/actionCreators';
import { cancelApp, checkPriorSubmittedApp, changeAppStatus, cloneApplication } from '@actions/individualPortal/dashboard/dashboardActionCreators';
import { filterOutPastTermEnrollments, filterOutEnrollees, checkAppIds } from '@utils/dashboard/processCommon';
import { getHealthEnrollments, processHealthEnrollments, getQHPIneligibleMembers } from '@utils/dashboard/processCommon';
import { configs } from '@constants/configs/configs';
import { dashboardModalTypes } from '@constants/individualPortal/dashboard/modals/dashboardModalTypes';
import { useNavigate } from 'react-router-dom';

const AppStatusContainer = (props) => {

    const {
        environment: {
            localDates: {
                serverDate
            },
            impersonation,
            impersonationDetails,
            stateCode,
            isState,
            user,
            links: {
                CALHEERS_URL
            },
            ssapFlowVersion,
            isTobaccoStatusUpdateWithoutSEP,
            cmrHouseholdId
        },
        dashboardModels,
        activeTab,
        showModal,
        cancelApp,
        checkPriorSubmittedApp,
        changeAppStatus,
        cloneApplication,
    } = props;

    const navigate = useNavigate();

    const householdCaseId = impersonation ? impersonationDetails && impersonationDetails.externalHouseholdId : user && user.externalHouseholdCaseId;
    const activeTabData = dashboardModels[activeTab];
    const activeTabDataHealthEnrollments = getHealthEnrollments(activeTabData.enrollments);
    const { goToStartApp, goToContinueApp, defaultReportAChange } = ctaHandlers;
    const userDetails = {
        householdCaseId,
        stateCode
    };

    /**
     * Depending on the ApplicationStatus handles what link and text to show.
     * @returns {{applicants: *, applicationYear: *}}
     * @param model
     */
    const getAppStatusData = (model) => {
        const { applicationStatus, applicants, applicationYear, openApplication } = model;
        let appStatusData = { applicants, applicationYear };

        const state_ID_NV_NJ_PA = isState.ID || isState.NV || isState.NJ || isState.PA;

        switch (applicationStatus) {
            case null:
            case 'CC':
            case 'CL':
                appStatusData = { ...appStatusData, status: APP_STATUS_MESSAGES.NOT_STARTED, links: [APP_STATUS_LINKS.START_APP] };
                break;

            case 'SU':
            case 'SG': {
                appStatusData.status = state_ID_NV_NJ_PA ?  APP_STATUS_MESSAGES.ERROR_PROCESSING_APP : APP_STATUS_MESSAGES.NOT_STARTED;
                appStatusData.links = state_ID_NV_NJ_PA ? [APP_STATUS_LINKS.EDIT_APP] : [APP_STATUS_LINKS.START_APP];
                break;
            }

            case 'OP': {
                appStatusData.status = openApplication ? APP_STATUS_MESSAGES.EDIT_IN_PROGRESS : APP_STATUS_MESSAGES.IN_PROGRESS;
                appStatusData.links = [APP_STATUS_LINKS.RESUME_APP, APP_STATUS_LINKS.CANCEL_APP];
                break;
            }
            case 'ER':
            case 'PN':
            case 'EN': {
                if (openApplication) {
                    appStatusData.status = APP_STATUS_MESSAGES.EDIT_IN_PROGRESS;
                    appStatusData.links = [APP_STATUS_LINKS.RESUME_APP, APP_STATUS_LINKS.CANCEL_APP];
                } else {
                    appStatusData.status = APP_STATUS_MESSAGES.COMPLETE;
                    appStatusData.links =  [APP_STATUS_LINKS.VIEW_APP];
                }

                break;
            }
            case 'QU':
                appStatusData = { ...appStatusData, status: APP_STATUS_MESSAGES.COMPLETE, links: [APP_STATUS_LINKS.VIEW_APP] };
                break;

            case 'UC':
            default: {
                appStatusData.status = APP_STATUS_MESSAGES.NOT_STARTED;
                appStatusData.links = state_ID_NV_NJ_PA ? [APP_STATUS_LINKS.RESUME_APP] : [APP_STATUS_LINKS.START_APP];
                break;
            }
        }

        return appStatusData;

    };

    const appStatusCTA = (stateCode, tabData, link) => {
        const { caseNumber, applicationStatus, applicationYear, isInsideOEWindow, isFinancial, ssapApplicationId, openApplication, encryptedApplicationId } = tabData;
        const ssapParams = { ssapFlowVersion, caseNumber, applicationStatus, applicationYear, isInsideOEWindow, isFinancial, ssapApplicationId, encryptedApplicationId,
            cmrHouseholdId: impersonation && impersonationDetails ? impersonationDetails.householdId : cmrHouseholdId };

        switch (link) {
            case APP_STATUS_LINKS.START_APP:
                goToStartApp(stateCode, ssapParams, CALHEERS_URL, householdCaseId, { showModal, checkPriorSubmittedApp });
                break;

            case APP_STATUS_LINKS.CONTINUE_APP:
            case APP_STATUS_LINKS.RESUME_APP:
                ssapParams.caseNumber = openApplication ? openApplication.caseNumber : caseNumber;
                goToContinueApp(stateCode, ssapParams, CALHEERS_URL, householdCaseId);
                break;

            case APP_STATUS_LINKS.VIEW_APP:
                navigate(configs.routes.APPLICATIONS);
                break;

            case APP_STATUS_LINKS.CANCEL_APP:
                showModal({
                    modalType: dashboardModalTypes.CANCEL_APP_MODAL,
                    modalData: { cb:  cancelApp.bind(null, configs.endpoints.CANCEL_APP, { caseNumber:  openApplication ? openApplication.caseNumber : caseNumber })
                    }
                });
                break;
            case APP_STATUS_LINKS.EDIT_APP:
                defaultReportAChange( ssapParams, { cloneApplication, changeAppStatus });
                break;
            default:
                break;
        }
    };

    const saveTobaccoDataToLocalStorage = (enrollments) => {
        activeTabDataHealthEnrollments && activeTabDataHealthEnrollments.length > 0 &&
        localStorage.setItem('tobaccoData', JSON.stringify(processHealthEnrollments(enrollments, serverDate)));
        localStorage.setItem('applicationYear', activeTabData.applicationYear);
    };

    const showTobaccoStatusLink = () => {
        let showTobaccoStatus = false;
        if (isTobaccoStatusUpdateWithoutSEP) {
            const QHPIneligibleMembers = getQHPIneligibleMembers(activeTabData.applicants);
            const filteredEnrollments = filterOutPastTermEnrollments(activeTabDataHealthEnrollments, serverDate);
            const tobaccoFilteredData = filterOutEnrollees(filteredEnrollments, activeTabData.tobaccoCoverageDate, QHPIneligibleMembers);

            showTobaccoStatus =  tobaccoFilteredData && tobaccoFilteredData.length > 0 && checkAppIds(activeTabDataHealthEnrollments, activeTabData.ssapApplicationId);
            showTobaccoStatus && saveTobaccoDataToLocalStorage(tobaccoFilteredData);
        }
        return showTobaccoStatus;
    };

    const appStatusData = getAppStatusData(activeTabData);

    return(
        <AppStatus
            data={appStatusData}
            onCTAHandler={(link) => appStatusCTA(stateCode, activeTabData,link)}
            householdIdDetails={userDetails}
            showTobaccoStatus={showTobaccoStatusLink()}
        />
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,
    dashboardModels: state.individualPortal.dashboard.dashboardModels,
    activeTab: state.individualPortal.dashboard.activeTab
});

export default connect(mapStateToProps, { showModal, cancelApp, checkPriorSubmittedApp, changeAppStatus, cloneApplication })(AppStatusContainer);